import axios from 'axios';

// Use relative URL for API calls (will be proxied by nginx)
const API_BASE_URL = '/api';

class AnalyticsService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getOverview(projectId, period = '30d') {
    try {
      const response = await this.api.get(`/analytics/overview/${projectId}?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics overview:', error);
      throw error;
    }
  }

  async getTrends(projectId, period = '30d') {
    try {
      const response = await this.api.get(`/analytics/trends/${projectId}?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching trends:', error);
      throw error;
    }
  }

  async getGeographicData(projectId) {
    try {
      const response = await this.api.get(`/analytics/geographic/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching geographic data:', error);
      throw error;
    }
  }

  async getProjectComparison(period = '30d') {
    try {
      const response = await this.api.get(`/analytics/comparison?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project comparison:', error);
      throw error;
    }
  }

  async getMonthlyReport(projectId, year = null) {
    try {
      const yearParam = year ? `?year=${year}` : '';
      const response = await this.api.get(`/analytics/monthly-report/${projectId}${yearParam}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      throw error;
    }
  }

  async exportCSV(projectId, period = '30d') {
    try {
      const response = await this.api.get(`/analytics/export/${projectId}/csv?period=${period}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'analytics_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error exporting CSV:', error);
      throw error;
    }
  }

  async healthCheck() {
    try {
      const response = await this.api.get('/analytics/health');
      return response.data;
    } catch (error) {
      console.error('Error checking analytics health:', error);
      throw error;
    }
  }
}

export default new AnalyticsService();